import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Loader2 } from "lucide-react";
import { api } from "@/lib/apiClient";
import { captureRef, storeGift } from "@/lib/referral";
import { primeBreath } from "@/lib/breathSound";
import ResetPlayer from "@/components/ResetPlayer";
import TheField from "@/components/TheField";
import Logo from "@/components/Logo";
import FoundingBadge from "@/components/partner/FoundingBadge";

// Public taster page for a reset a member shared.
export default function Gift() {
  const { rid } = useParams();
  const ref = new URLSearchParams(window.location.search).get("ref") || "";
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [finished, setFinished] = useState(false);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    captureRef();
    if (ref) storeGift(rid);
    api.get(`/gift/${rid}`, { params: { ref } }).then((r) => setData(r.data)).catch(() => setFailed(true));
  }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return <Shell><p className="text-muted-foreground" data-testid="gift-error">This reset link isn't available any more.</p><Cta /></Shell>;
  if (!data) return <Shell><Loader2 className="w-6 h-6 animate-spin text-primary" /></Shell>;
  return (
    <Shell>
      <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-4" data-testid="gift-from">{data.from ? `${data.from} shared this with you` : "A reset for you"}</p>
      {data.founding && <FoundingBadge className="-mt-2 mb-5" />}
      <h1 className="font-serif text-4xl sm:text-5xl mb-3" data-testid="gift-title">{data.title}</h1>
      {finished ? (
        <div className="mb-10 max-w-sm" data-testid="gift-ending">
          <p className="font-serif text-2xl">{data.words?.ending_title || "Feeling different?"}</p>
          <p className="text-muted-foreground mt-2">{data.words?.ending_sub || "This is a small part of Xcelio."}</p>
        </div>
      ) : (
        <p className="text-muted-foreground mb-10 max-w-sm" data-testid="gift-sub">{data.words?.sub || "Sometimes you don't need advice. You just need to come back to yourself."}<span className="block text-xs mt-2">90 seconds with Adelaida. Headphones help.</span></p>
      )}
      {!finished ? (
        <button onClick={() => { primeBreath(); setPlaying(true); }} className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-105 active:scale-95 transition-transform duration-200" data-testid="gift-play-btn" aria-label="Play reset">
          <Play className="w-7 h-7 ml-1" fill="currentColor" />
        </button>
      ) : <Cta big label={data.words?.ending_cta} />}
      {!finished && <div className="mt-12"><Cta /></div>}
      {playing && <ResetPlayer reset={{ id: rid, title: data.title }} load={() => Promise.resolve(data)} onClose={() => { setPlaying(false); setFinished(played); }} onDone={() => setPlayed(true)} />}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background grain flex flex-col items-center justify-center text-center px-6 py-16 relative overflow-hidden" data-testid="gift-page">
    <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none"><TheField intensity={0.5} size={520} active><span /></TheField></div>
    <div className="relative flex flex-col items-center">
      <div className="mb-10"><XcelioMark /></div>
      {children}
      <p className="text-xs text-muted-foreground mt-10 max-w-xs" data-testid="gift-about">Xcelio is a personal confidence coach for men. Don't become more impressive. Become more you.</p>
    </div>
  </div>
);

export const XcelioMark = () => (
  <Link to="/" data-testid="xcelio-mark" className="inline-flex"><Logo className="text-3xl" showDot /></Link>
);

const Cta = ({ big, label }) => (
  <Link to={`/register${window.location.search}`} data-testid={big ? "gift-cta-main" : "gift-cta"}
    className={big ? "px-8 py-3.5 rounded-full bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity duration-200" : "text-sm text-primary hover:underline"}>
    {big ? `${label || "Meet your Coach"} →` : "What is Xcelio? →"}
  </Link>
);
